import React, { useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import toMoment from '~/lib/toMoment';
import loadable from '@loadable/component';
import widgetSchemas from 'shared/widgets/schemas';
import { flashMessage } from '~/app/actions';
import { describeComponent } from '~/pages/components/PageEditor/shell/outline';
import ComponentBrowser from '~/pages/components/PageEditor/ComponentBrowser';
import EditComponent from '~/pages/components/PageEditor/EditComponent';
import { pagePublicPath } from '~/lib/pagePublicPath';
import { S, A } from '~/app/ui/tokens';
import { tr } from '~/app/ui/lang';
import Button from '~/app/ui/Button';
import { Page, PageHeader, Toolbar, Spacer } from '~/app/ui/Page';
import SearchField from '~/app/ui/SearchField';
import EmptyState from '~/app/ui/EmptyState';
import Dialog from '~/app/ui/Dialog';
import ConfirmDialog from '~/app/ui/ConfirmDialog';
import { RowMenu } from '~/app/ui/Menu';
import { Table, SkeletonRows, rowClick, useRowClasses } from '~/app/ui/Table';
import AddPresetTitle from './AddPresetTitle';

const PreviewPreset = loadable(() => import(/* webpackChunkName: "PreviewPreset" */ './PreviewPreset'));

// Same name as before: the preset actions refetch 'getPresets'.
const PRESETS = gql`
  query getPresets($widgetId: String) {
    widgetPresets(widgetId: $widgetId) {
      _id
      widgetId
      title
      createdAt
      updatedAt
      createdBy
      updatedBy
      props
    }
  }
`;
const USAGE = gql`query getPresetUsage { widgetPresetUsage { presetId pages } }`;
const PAGES_USING = gql`query getPagesUsingPreset($presetId: ID!) { pagesUsingPreset(presetId: $presetId) { _id title path } }`;
const DELETE = gql`mutation deleteWidgetPreset($presetId: ID!) { deleteWidgetPreset(presetId: $presetId) { status message } }`;
const EDIT = gql`mutation editWidgetPreset($presetId: ID!, $props: JSON!) { editWidgetPreset(presetId: $presetId, props: $props) { status message } }`;

type Preset = {
  _id: string;
  widgetId: string;
  title: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
  createdBy?: string | null;
  props?: Record<string, unknown>;
};

type Modal =
  | { kind: 'browser' }
  | { kind: 'edit'; preset: Preset }
  | { kind: 'new'; draft: Preset }
  | { kind: 'title'; draft: Preset }
  | { kind: 'preview'; preset: Preset }
  | { kind: 'pages'; preset: Preset }
  | { kind: 'delete'; preset: Preset }
  | null;

const useStyles = createUseStyles({
  filter: {
    height: 36,
    padding: '0 30px 0 10px',
    border: `1px solid ${S.fieldLine}`,
    borderRadius: A.radius,
    background: `${A.surface} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%236B6D72'/%3E%3C/svg%3E") no-repeat right 11px center`,
    appearance: 'none',
    fontFamily: S.ui,
    fontSize: 14,
    color: S.fg,
    maxWidth: 260,
    cursor: 'pointer',
    '&:focus-visible': A.focus
  },
  title: {
    all: 'unset',
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline', textUnderlineOffset: 3 },
    '&:focus-visible': A.focus
  },
  widget: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: 13, color: S.muted },
  tag: {
    padding: '1px 6px',
    border: `1px solid ${S.fieldLine}`,
    borderRadius: 4,
    fontFamily: S.label,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase'
  },
  colUsage: { width: 150 },
  colUpdated: { width: 190, '@media (max-width: 760px)': { display: 'none' } },
  colMenu: { width: 52, textAlign: 'right' },
  usage: {
    all: 'unset',
    fontSize: 14,
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline', textUnderlineOffset: 3 },
    '&:focus-visible': A.focus
  },
  unused: { fontSize: 14, color: S.muted },
  when: { whiteSpace: 'nowrap' },
  who: { fontSize: 12, color: S.muted, marginTop: 2 },
  hidden: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' },
  pageList: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 2 },
  pageItem: {
    display: 'grid',
    gap: 2,
    padding: '8px 10px',
    margin: '0 -10px',
    borderRadius: 8,
    textDecoration: 'none',
    color: S.fg,
    '&:hover': { background: S.chrome },
    '&:focus-visible': A.focus,
    '& b': { fontWeight: 600 },
    '& span': { fontFamily: S.mono, fontSize: 12, color: S.muted }
  }
});

const ensureOk = (r?: { status: number; message?: string } | null) => {
  if (!r || r.status !== 200) throw new Error((r && r.message) || 'That didn’t go through. Try again.');
};

/** What a preset is made from, e.g. "Hero banner" or "RoboticsSupport · Custom · latest". */
const widgetOf = (p: Preset) => describeComponent({ widgetId: p.widgetId, props: p.props as Record<string, never> }, [], widgetSchemas as never);

const PagesUsing: React.FC<{ preset: Preset; onClose: () => void }> = ({ preset, onClose }) => {
  const c = useStyles();
  const { data, loading, error } = useQuery(PAGES_USING, { variables: { presetId: preset._id }, fetchPolicy: 'network-only' });
  const pages: Array<{ _id: string; title: string; path: string }> = data?.pagesUsingPreset || [];
  return (
    <Dialog title={`Pages using “${preset.title}”`} onClose={onClose} testId="preset-pages">
      {loading && <p>Loading…</p>}
      {error && <p>Couldn’t load the pages: {error.message}</p>}
      {!loading && !error && pages.length === 0 && <p>No page uses this preset.</p>}
      {pages.length > 0 && (
        <ul className={c.pageList}>
          {pages.map(p => (
            <li key={p._id}>
              <Link className={c.pageItem} to={`/admin/page/${p._id}`}>
                <b>{p.title}</b>
                <span>{pagePublicPath(p.path)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
};

/** Deleting a preset some page uses is refused, so this shows those pages instead of a Delete button. */
const DeletePreset: React.FC<{ preset: Preset; onDelete: () => Promise<void>; onClose: () => void }> = ({ preset, onDelete, onClose }) => {
  const c = useStyles();
  const { data, loading, error } = useQuery(PAGES_USING, { variables: { presetId: preset._id }, fetchPolicy: 'network-only' });
  const pages: Array<{ _id: string; title: string; path: string }> = data?.pagesUsingPreset || [];
  if (!loading && pages.length > 0) {
    return (
      <Dialog title={`“${preset.title}” is still in use`} onClose={onClose} testId="preset-in-use" footer={<Button onClick={onClose}>Close</Button>}>
        <p style={{ marginTop: 0 }}>Remove it from {pages.length === 1 ? 'this page' : `these ${pages.length} pages`} first. A page counts if its draft or its live version uses the preset.</p>
        <ul className={c.pageList}>
          {pages.map(p => (
            <li key={p._id}>
              <Link className={c.pageItem} to={`/admin/page/${p._id}`}>
                <b>{p.title}</b>
                <span>{pagePublicPath(p.path)}</span>
              </Link>
            </li>
          ))}
        </ul>
      </Dialog>
    );
  }
  return (
    <ConfirmDialog
      danger
      title={`Delete the preset “${preset.title}”?`}
      confirmLabel="Delete preset"
      confirmDisabled={loading}
      onConfirm={onDelete}
      onClose={onClose}
      testId="preset-delete"
    >
      <p>
        {loading && 'Checking where it’s used…'}
        {!loading && error && `Couldn’t check where it’s used (${error.message}). If a page still uses it, it won’t be deleted. Deleting can’t be undone.`}
        {!loading && !error && 'No page uses it. Deleting can’t be undone.'}
      </p>
    </ConfirmDialog>
  );
};

const PresetsScreen: React.FC = () => {
  const c = useStyles();
  const rowCls = useRowClasses();
  const dispatch = useDispatch();
  const { data, loading, error, refetch } = useQuery(PRESETS, { fetchPolicy: 'cache-and-network' });
  const usage = useQuery(USAGE, { fetchPolicy: 'cache-and-network' });
  const refetchQueries = ['getPresets', 'getPresetUsage'];
  const [deletePreset] = useMutation(DELETE, { refetchQueries, awaitRefetchQueries: true });
  const [editPreset] = useMutation(EDIT, { refetchQueries });
  const [q, setQ] = useState('');
  const [widget, setWidget] = useState('');
  const [modal, setModal] = useState<Modal>(null);

  const presets: Preset[] = useMemo(() => data?.widgetPresets || [], [data]);
  const usageBy = useMemo(() => {
    const m = new Map<string, number>();
    (usage.data?.widgetPresetUsage || []).forEach((u: { presetId: string; pages: number }) => m.set(String(u.presetId), u.pages));
    return m;
  }, [usage.data]);
  const widgets = useMemo(() => {
    const names = new Map<string, string>();
    presets.forEach((p) => { if (!names.has(p.widgetId)) names.set(p.widgetId, describeComponent({ widgetId: p.widgetId }, [], widgetSchemas as never).title); });
    return Array.from(names, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [presets]);
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return presets
      .filter(p => !widget || p.widgetId === widget)
      .filter((p) => {
        if (!needle) return true;
        const w = widgetOf(p);
        return [p.title, p.widgetId, w.title].some(x => String(x || '').toLowerCase().includes(needle));
      })
      .sort((a, b) => (toMoment(b.updatedAt)?.valueOf() || 0) - (toMoment(a.updatedAt)?.valueOf() || 0));
  }, [presets, q, widget]);

  const saveEdit = async (preset: Preset, next: { props: Record<string, unknown> }) => {
    try {
      const r = await editPreset({ variables: { presetId: preset._id, props: next.props } });
      ensureOk(r.data?.editWidgetPreset);
      dispatch(flashMessage(`Saved “${preset.title}”. Pages using it show the change when they’re next deployed.`));
    } catch (e) {
      dispatch(flashMessage((e as Error).message, 'ERROR'));
    }
  };

  const remove = async (preset: Preset) => {
    const r = await deletePreset({ variables: { presetId: preset._id } });
    ensureOk(r.data?.deleteWidgetPreset);
    dispatch(flashMessage(`Deleted the preset “${preset.title}”.`));
  };

  let body: React.ReactNode;
  if (error && !data) {
    body = <tr><td colSpan={4}><EmptyState tone="error" title="Couldn’t load presets" action={<Button icon="refresh" onClick={() => refetch()}>Try again</Button>}>{error.message}</EmptyState></td></tr>;
  } else if (loading && !data) {
    body = <SkeletonRows rows={6} widths={['55%', 70, 110, 0]} />;
  } else if (shown.length === 0) {
    body = (
      <tr><td colSpan={4}>
        {q || widget
          ? <EmptyState icon="search_off" title="No presets match" action={<Button onClick={() => { setQ(''); setWidget(''); }}>Clear filters</Button>} />
          : <EmptyState icon="bookmarks" title="No presets yet" action={<Button variant="primary" icon="add" onClick={() => setModal({ kind: 'browser' })}>New preset</Button>}>A preset saves a widget’s settings so every page can use the same ones.</EmptyState>}
      </td></tr>
    );
  } else {
    body = shown.map((p) => {
      const w = widgetOf(p);
      const used = usage.data ? (usageBy.get(String(p._id)) || 0) : null;
      const updated = toMoment(p.updatedAt);
      return (
        <tr key={p._id} className={rowCls.clickable} {...rowClick(() => setModal({ kind: 'edit', preset: p }))} data-preset={p._id}>
          <td>
            <button type="button" className={c.title} onClick={() => setModal({ kind: 'edit', preset: p })}>{p.title}</button>
            <div className={c.widget}>
              {w.title}
              {w.tag && <span className={c.tag}>{w.tag}</span>}
              {w.detail && <span>{w.detail}</span>}
            </div>
          </td>
          <td className={c.colUsage}>
            {used === null ? <span className={c.unused}>…</span>
              : used === 0 ? <span className={c.unused}>No drafts</span>
                : <button type="button" className={c.usage} onClick={() => setModal({ kind: 'pages', preset: p })} data-testid="preset-usage">{used} {used === 1 ? 'page' : 'pages'}</button>}
          </td>
          <td className={c.colUpdated}>
            {updated ? <div className={c.when} title={updated.format('MMM D, YYYY h:mm A')}>{moment().diff(updated, 'days') < 7 ? updated.fromNow() : updated.format('MMM D, YYYY')}</div> : '—'}
            {p.updatedBy && <div className={c.who}>by {p.updatedBy}</div>}
          </td>
          <td className={c.colMenu}>
            <RowMenu
              label={`Actions for ${p.title}`}
              testId={`preset-menu-${p._id}`}
              groups={[
                [
                  { key: 'edit', label: 'Edit settings', icon: 'tune', onSelect: () => setModal({ kind: 'edit', preset: p }) },
                  { key: 'preview', label: 'Preview', icon: 'visibility', onSelect: () => setModal({ kind: 'preview', preset: p }) },
                  { key: 'pages', label: 'Pages using it', icon: 'description', onSelect: () => setModal({ kind: 'pages', preset: p }) }
                ],
                [{ key: 'delete', label: 'Delete…', icon: 'delete_outline', danger: true, onSelect: () => setModal({ kind: 'delete', preset: p }) }]
              ]}
            />
          </td>
        </tr>
      );
    });
  }

  return (
    <Page testId="presets-screen">
      <PageHeader
        title={tr('Widget presets', '预设插件')}
        description="Saved settings for a widget that many pages share. Editing a preset changes every page that uses it when those pages are next deployed."
        actions={<Button variant="primary" icon="add" onClick={() => setModal({ kind: 'browser' })} data-testid="new-preset">New preset</Button>}
      />
      <Toolbar>
        <SearchField value={q} onChange={setQ} placeholder="Search presets" testId="presets-search" />
        {widgets.length > 1 && (
          <select className={c.filter} value={widget} onChange={e => setWidget(e.target.value)} aria-label="Widget">
            <option value="">All widgets</option>
            {widgets.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        )}
        <Spacer />
      </Toolbar>
      <Table label="Widget presets" testId="presets-table">
        <thead>
          <tr>
            <th>Preset</th>
            <th className={c.colUsage} title="Pages whose current draft uses the preset. Deleting also checks live versions.">Used on</th>
            <th className={c.colUpdated}>Updated</th>
            <th className={c.colMenu}><span className={c.hidden}>Actions</span></th>
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </Table>

      {modal?.kind === 'browser' && (
        <ComponentBrowser
          onSelect={(component: Preset) => setModal({ kind: 'new', draft: { ...component } })}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'new' && (
        <EditComponent
          instance={modal.draft}
          onChange={(next: { props: Record<string, unknown> }) => setModal(m => (m && m.kind === 'new' ? { kind: 'new', draft: { ...m.draft, props: { ...next.props } } } : m))}
          onContinue={() => setModal(m => (m && m.kind === 'new' ? { kind: 'title', draft: m.draft } : m))}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'title' && (
        <AddPresetTitle widgetPreset={modal.draft} onClose={() => { setModal(null); usage.refetch(); }} />
      )}
      {modal?.kind === 'edit' && (
        <EditComponent
          instance={modal.preset}
          onChange={(next: { props: Record<string, unknown> }) => saveEdit(modal.preset, next)}
          onContinue={() => setModal(null)}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'preview' && <PreviewPreset preset={modal.preset} onClose={() => setModal(null)} />}
      {modal?.kind === 'pages' && <PagesUsing preset={modal.preset} onClose={() => setModal(null)} />}
      {modal?.kind === 'delete' && <DeletePreset preset={modal.preset} onDelete={() => remove(modal.preset)} onClose={() => setModal(null)} />}
    </Page>
  );
};

export default PresetsScreen;
